import {
  DocumentMetadataReferenceDocumentItem,
  DocumentMetadataSectionItem,
} from '@/components/document-edit/DocumentMetadata'
import { DocumentMetadataSection } from '@/components/document-edit/DocumentMetadata'
import { Separator } from '@/components/ui/separator'
import { createFileRoute, useParams } from '@tanstack/react-router'
import { useDocumentStore } from '@/stores/documentStore'
import { CategoryTag, type DocumentMetadata } from '@/data/entities'
import { LoadingDocumentEditContent } from '@/components/loading-ui/LoadingDocument'
import { memo } from 'react'

export const Route = createFileRoute(
  '/_authenticated/org/$orgId/projects/$projectId/sops/$sopId/insights',
)({
  component: memo(RouteComponent),
  pendingComponent: () => <LoadingDocumentEditContent />,
})

function RouteComponent() {
  // TODO: handle conditional for edit access
  const { orgId, projectId } = useParams({
    from: '/_authenticated/org/$orgId/projects/$projectId/sops/$sopId/insights',
  })
  const documentData = useDocumentStore((state) => state.documentData)
  const isEditing = useDocumentStore((state) => state.isEditing)
  const setMetadataKey = useDocumentStore((state) => state.setMetadataKey)

  const handleChangeDocumentData = (
    key: string,
    newValue: DocumentMetadata[keyof DocumentMetadata],
  ) => {
    setMetadataKey(key, String(newValue ?? ''))
  }

  // TODO: let this map over the metadata object instead of hardcoding the keys
  return (
    <div className="flex flex-col gap-4 pb-12">
      {documentData.metadata && (
        <>
          <DocumentMetadataSection title="Engagement">
            <div className="col-span-2 flex flex-col gap-2">
              <DocumentMetadataSectionItem
                label="Total Views"
                value={documentData.metadata.views ?? 0}
                isEditing={false}
              />
            </div>
          </DocumentMetadataSection>
          <Separator />
          <DocumentMetadataSection title="Document Control">
            <div className="col-span-2 flex flex-col gap-2">
              <DocumentMetadataSectionItem
                label="Document #"
                value={documentData.metadata.documentNumber}
                isEditing={isEditing}
                onChange={(newValue) => {
                  handleChangeDocumentData('documentNumber', newValue)
                }}
              />
              <DocumentMetadataSectionItem
                label="Version"
                value={documentData.metadata.version}
                isEditing={isEditing}
                onChange={(newValue) => {
                  handleChangeDocumentData('version', newValue)
                }}
              />
              <DocumentMetadataSectionItem
                label="Standard"
                value={documentData.metadata.standard}
                isEditing={isEditing}
                onChange={(newValue) => {
                  handleChangeDocumentData('standard', newValue)
                }}
              />
              <DocumentMetadataSectionItem
                label="Effective Date"
                value={documentData.metadata.effectiveDate}
                isEditing={isEditing}
                onChange={(newValue) => {
                  handleChangeDocumentData('effectiveDate', newValue)
                }}
              />
              <DocumentMetadataSectionItem
                label="Expiration Date"
                value={documentData.metadata.expirationDate}
                isEditing={isEditing}
                onChange={(newValue) => {
                  handleChangeDocumentData('expirationDate', newValue)
                }}
              />
              <DocumentMetadataSectionItem
                label="Document Type"
                value={documentData.metadata.type}
                type="tag"
                isEditing={isEditing}
                onChange={(newValue) => {
                  handleChangeDocumentData('type', newValue)
                }}
              />
              <DocumentMetadataSectionItem
                label="Workspace Category"
                value={documentData.metadata.category || CategoryTag.Others}
                type="category"
                isEditing={isEditing}
                onChange={(newValue) => {
                  handleChangeDocumentData('category', newValue)
                }}
              />
            </div>
          </DocumentMetadataSection>
          <Separator />
          <DocumentMetadataSection title="Files Attached">
            <div className="col-span-2 flex flex-col gap-2">
              {documentData.documentRefs?.map((file) => (
                <DocumentMetadataReferenceDocumentItem
                  key={file.id}
                  referenceDocument={file}
                  tenantId={orgId}
                  projectId={projectId}
                />
              ))}
            </div>
          </DocumentMetadataSection>
          <Separator />
          {documentData.customPropmpt ? (
            <DocumentMetadataSection title="Custom Prompt">
              <p className="col-span-2 font-normal">
                {documentData.customPropmpt}
              </p>
            </DocumentMetadataSection>
          ) : null}
        </>
      )}
    </div>
  )
}
